import { Button, HStack, VStack } from '@chakra-ui/react';
import { useState } from 'react';

import { TCredentialCreate, TSystemCredential } from '@/types/credentials';

import CustomTextField from '../CustomTextField/CustomTextField';

const emptyFormData = {
  customName: '',
  properties: {} as Record<string, string>,
};

export interface CreateCredentialFormProps {
  credential: TSystemCredential;
  onSubmit: (data: TCredentialCreate) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
}

const formatFieldLabel = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

const CreateCredentialForm = ({
  credential,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Create Credential',
}: CreateCredentialFormProps) => {
  const [formData, setFormData] = useState(emptyFormData);

  const handleFieldChange = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      properties: { ...prev.properties, [key]: value },
    }));
  };

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, customName: value }));
  };

  const handleSubmit = async () => {
    const data: TCredentialCreate = {
      type: credential.type,
      name: credential.name,
      customName: formData.customName,
      properties: Object.entries(formData.properties).map(([key, value]) => ({
        propertyName: key,
        value: value as string,
      })),
    };
    await onSubmit(data);
    setFormData(emptyFormData);
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    onCancel();
  };

  const isFormValid = () => {
    if (!formData.customName.trim()) {return false;}

    return Object.entries(credential.schema).every(([key, fieldSchema]) => {
      if (fieldSchema.required) {return !!formData.properties[key]?.trim();}

      return true;
    });
  };

  return (
    <VStack gap={6} align="stretch" width="100%">
      <CustomTextField
        value={formData.customName}
        onChange={handleNameChange}
        placeholder="Type name here"
        label="Credential Name"
        required
      />
      {Object.entries(credential?.schema || {}).map(([key, fieldSchema]) => (
        <CustomTextField
          key={key}
          value={formData.properties[key] || ''}
          onChange={(value) => handleFieldChange(key, value)}
          placeholder={`Type ${formatFieldLabel(key).toLowerCase()} here`}
          label={formatFieldLabel(key)}
          type="text"
          required={fieldSchema.required}
          width="100%"
          maxWidth="100%"
        />
      ))}
      <HStack gap={3} justifyContent="flex-end" w="100%">
        <Button
          onClick={handleCancel}
          variant="outline"
          fontSize={14}
          px={4}
          py={2.5}
          fontWeight={600}
          color="purple.fg"
          borderColor="purple.fg"
          _hover={{ backgroundColor: 'purple.50' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || isLoading}
          backgroundColor="purple.fg"
          color="white"
          fontSize={14}
          px={4}
          py={2.5}
          fontWeight={600}
          borderRadius={4}
          border="1px solid"
          borderColor="purple.muted"
          _hover={{ backgroundColor: 'purple.600' }}
          loading={isLoading}
          loadingText="Creating..."
        >
          {submitLabel}
        </Button>
      </HStack>
    </VStack>
  );
};

export default CreateCredentialForm;
